import React, { useEffect, useState } from "react";
import { displayFileIcon } from "../../../../../helpers";
import { getFileTypeFromUrl } from "../../../../../helpers/getFileTypeFromUrl";
import Button from "../../../../common/Button";
import { firebaseService } from "services/firebaseService";
import { productionActions } from "redux/Ldb/actions";
import { useAppDispatch } from "hooks/storeHooks";
import { useParams } from "react-router-dom";
import Loader from "components/common/Loader";
import { isFileSizeLessThanOrEqualTo } from "helpers/compareFileSizeInMb";
import CustomToast from "components/common/CustomToast";
import toast from "react-hot-toast";

type documentProps = {
    attachments: {
        filename: string;
        path: string;
        documentType: string;
    }[];
    type?: string;
};

const AttachedDocuments = ({ attachments, type }: documentProps) => {
    const dispatch = useAppDispatch();
    const { productionId } = useParams();

    const [deletingDocument, setDeletingDocument] = useState(false);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [documents, setDocuments] = useState<{ [key: string]: any }[]>([]);
    const [replacingDocument, setReplacingDocument] = useState(false);
    const [saveAttachment, setSaveAttachment] = useState(false);
    const [docIdx, setDocIdx] = useState<number>(-1);
    const [replaceIdx, setReplaceIdx] = useState<number>(-1);

    useEffect(() => {
        if (saveAttachment) {
            const body = {
                attachments: documents?.length >= 1 ? [...documents] : [],
            };
            dispatch(productionActions?.updateProductionPlan(body, productionId));
            setSaveAttachment(false);
        }
    }, [saveAttachment]);

    useEffect(() => {
        setDocuments(
            attachments?.length > 0
                ? attachments?.map((item) => ({
                      filename: item?.filename,
                      path: item?.path,
                      documentType: item?.documentType,
                  }))
                : []
        );
    }, [attachments]);

    const handleReplaceUpload = (indexToReplace: number, newFileName: string, newFile: File) => {
        setReplacingDocument(true);
        firebaseService.uploadFile(
            `synthally/${process.env.REACT_APP_STAGE ?? "staging"}/production/${newFileName}`,
            newFile,
            (url) => {
                setDocuments((prevAttachments) => {
                    const updatedAttachments = [...prevAttachments];
                    updatedAttachments[indexToReplace] = {
                        filename: newFileName,
                        path: url,
                        documentType: "production_document",
                    };
                    return updatedAttachments;
                });
                setSaveAttachment(true);
            }
        );
        setReplaceIdx(-1);
        setReplacingDocument(false);
    };
    const handleUpload = (fileName: string, file: File) => {
        if (fileName && file) {
            setUploadingDocument(true);
            firebaseService.uploadFile(
                `synthally/${process.env.REACT_APP_STAGE ?? "staging"}/shipment-doc/${fileName}`,
                file,
                (url) => {
                    setDocuments((prev) => [
                        ...prev,
                        { path: url, filename: fileName, documentType: "production_document" },
                    ]);
                    setSaveAttachment(true);
                }
            );
            setUploadingDocument(false);
        }
    };
    const handleRemoveUpload = (idx) => {
        const newArr = [...documents];

        const imagePath = decodeURIComponent(
            documents[idx]?.path?.split("/o/")?.[1]?.split("?alt")?.[0]
        );
        setDeletingDocument(true);
        firebaseService.removeFile(imagePath, () => {
            newArr?.splice(idx, 1);
            setDocuments(newArr);
            setDocIdx(-1);
            setSaveAttachment(true);
        });

        setDeletingDocument(false);
    };
    return (
        <div>
            <div className="gap-1  pb-4">
                <p className="text-gm-75 text-base mb-1">Attached documents to production plan</p>
                <p className="text-slate-500 text-sm">
                    {documents?.length > 0
                        ? "Additional files, such as sheets and reference documents, were attached to this production plan. You can view them here for reference."
                        : "No attachment"}
                </p>
            </div>

            {documents?.length > 0 && (
                <div className="px-4  pt-4 rounded-md border border-b-slate-200 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]">
                    <p className="border-b border-b-slate-200 mb-4">
                        {documents?.length} document(s)
                    </p>
                    {documents?.map((item, idx) => {
                        return (
                            <div
                                className={`flex items-center justify-between py-3  ${
                                    documents?.length - 1 !== idx && "border-b border-b-slate-200 "
                                }  `}
                                key={idx}
                            >
                                <div
                                    className="flex items-center gap-2 cursor-pointer"
                                    onClick={() => {
                                        const a = document.createElement("a");
                                        a.href = item?.path;
                                        a.target = "_blank";
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                    }}
                                >
                                    {displayFileIcon(
                                        getFileTypeFromUrl(item?.path),
                                        false,
                                        "!w-[32px] !h-[32px]"
                                    )}
                                    <div className="font-rocGroteskMedium">
                                        <p className="text-sm">
                                            {item?.filename}
                                            {` `}
                                        </p>
                                    </div>
                                </div>
                                {type === "plan" && (
                                    <div className="flex items-center gap-2">
                                        <label
                                            className={`!border !text-sm rounded !border-n-40 !w-fit !py-2 !px-4 !bg-transparent !text-gm-50  ${
                                                !deletingDocument && "cursor-pointer"
                                            }`}
                                            htmlFor={`replaceFile${idx}`}
                                        >
                                            {idx === replaceIdx && replacingDocument ? (
                                                <Loader color={"inherit"} size={4} />
                                            ) : (
                                                " Replace File"
                                            )}
                                        </label>
                                        <input
                                            id={`replaceFile${idx}`}
                                            type="file"
                                            className={`opacity-0 hidden w-full h-full top-0 ${
                                                !replacingDocument && "cursor-pointer"
                                            }`}
                                            accept="*"
                                            disabled={replacingDocument}
                                            onChange={(evt) => {
                                                if (
                                                    !isFileSizeLessThanOrEqualTo(
                                                        evt.target.files?.[0],
                                                        25
                                                    )
                                                ) {
                                                    toast.custom((t) => (
                                                        <CustomToast
                                                            t={t}
                                                            message={"File too large"}
                                                            type={"error"}
                                                        />
                                                    ));
                                                    return;
                                                }

                                                if (evt.target.files?.[0]) {
                                                    setReplaceIdx(idx);

                                                    handleReplaceUpload(
                                                        idx,
                                                        evt.target.files?.[0].name,
                                                        evt.target.files?.[0]
                                                    );
                                                }
                                            }}
                                        />

                                        <button
                                            type={"button"}
                                            className="!border !text-sm rounded !border-n-40 !w-fit !py-2 !px-4 !bg-transparent !text-[#FF0000]"
                                            onClick={(e) => {
                                                e?.stopPropagation();
                                                handleRemoveUpload(idx);
                                                setDocIdx(idx);
                                            }}
                                        >
                                            {idx === docIdx && deletingDocument ? (
                                                <Loader color={"inherit"} size={4} />
                                            ) : (
                                                "Remove"
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AttachedDocuments;
